import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { virtualHumanDataSelector } from '../../slices/VirtualHumanSlice';
import { Heading } from '../atoms/Heading/Heading.styled';
import Paragraph from '../atoms/Paragraph/Paragraph';
import VHProfileImage, {
  AvatarSizes,
} from '../atoms/VHProfileImage/VHProfileImage';
import CardModal from '../molecules/CardModal/CardModal';
import * as Styled from './LegacyVHInfoPopup.styled';
import {
  selectChatState,
  ChatStates,
  isPreviewPlayerSelector,
} from '../../slices/chat/ChatSlice';
import { Button } from '@virtidev/toolbox';
import { useVHTypeConfig } from '../vh-types/vhTypeHooks';
import { patientSelector } from '../../slices/PatientSlice';
import tracker from '../../../../core/src/helpers/tracker';
import { useVhReady } from '@vh/components/VHInfoPopup/useVhReady';
import { useVHPlayerConfig } from '@vh/components/vh-types/vhPlayerHooks';
import { isEmbeddedPlayerSelector, moreInfoVisibleSelector, setMoreInfoVisible } from '@vh/slices/vh-player/VHPlayerSlice';

/** @type {React.FC<{vhId: number; onBeforeStart: () => Promise<void>; avatarComponent?: React.ReactNode}>} */
const LegacyVHInfoPopup = ({ vhId, onBeforeStart, avatarComponent }) => {
  const moreInfoVisible = useSelector(moreInfoVisibleSelector);
  const vh = useSelector(virtualHumanDataSelector);
  const vhDBVH = useSelector(patientSelector);
  const dispatch = useDispatch();
  const onModalClose = useCallback(() => {
    dispatch(setMoreInfoVisible(false));
  }, [dispatch]);
  const [vhReady, hasChecked] = useVhReady(vhId, vh.Type);
  const chatState = useSelector(selectChatState);
  const vhTypeConfig = useVHTypeConfig();
  const isEmbedded = useSelector(isEmbeddedPlayerSelector);
  const isPreviewPlayer = useSelector(isPreviewPlayerSelector);
  const [runningPreStartLoad, setRunningPreStartLoad] = useState(false);
  const playerConfig = useVHPlayerConfig();

  const handleClickStart = useCallback(async () => {
    if (onBeforeStart) {
      setRunningPreStartLoad(true);
      try {
        await onBeforeStart();
        setRunningPreStartLoad(false);
      } catch (e) {
        setRunningPreStartLoad(false);
        return;
      }
    }
    if (playerConfig.conversationControl === 'standard') {
      dispatch(vhTypeConfig.getGreetingThunk(vhDBVH)(vhId));
    }
    // can't run event if embedded due to Cross origin iframe usage
    if (!isEmbedded) {
      var event = new CustomEvent('onVHSessionStart');
      window.parent.document.dispatchEvent(event);
    }
    onModalClose();

    if (isPreviewPlayer) {
      tracker.track('vh_preview', {
        ID: +vh.ID,
        ExternalID: vhId,
      });
    }
  }, [
    onBeforeStart,
    playerConfig?.conversationControl,
    isEmbedded,
    onModalClose,
    isPreviewPlayer,
    dispatch,
    vhTypeConfig,
    vhDBVH,
    vhId,
    vh.ID,
  ]);

  return (
    <>
      <CardModal
        show={moreInfoVisible}
        onHide={chatState === ChatStates.LOADING ? undefined : onModalClose}
      >
        <Styled.ModalContent>
          <Styled.AvatarWrapper>
            {avatarComponent ?? (
              <VHProfileImage src={vh?.ThumbnailURL} size={AvatarSizes.LARGE} />
            )}
          </Styled.AvatarWrapper>
          <Heading level="2">{vh?.Title}</Heading>
          <Paragraph>{vh?.Description}</Paragraph>

          {chatState === ChatStates.LOADING && (
            <Styled.StartButtonWrapper>
              <Button
                loading={!vhReady || runningPreStartLoad}
                disabled={!vhReady}
                color="turquoise"
                onClick={handleClickStart}
              >
                Start session
              </Button>
            </Styled.StartButtonWrapper>
          )}
          {hasChecked && !vhReady && (
            <div>
              This virtual human is currently training, please wait or come back
              later.
            </div>
          )}
        </Styled.ModalContent>
      </CardModal>
    </>
  );
};

LegacyVHInfoPopup.propTypes = {
  vhId: PropTypes.number.isRequired,
  onBeforeStart: PropTypes.func.isRequired,
};

export default LegacyVHInfoPopup;
